import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Trash2 } from "lucide-react";

const COUNTDOWN_SECONDS = 10;

const DEFAULT_DESCRIPTION =
  "Các giấy chứng nhận đã cấp từ yêu cầu này (nếu có) sẽ bị thu hồi và không còn hiển thị ở tab Chứng nhận đã cấp. Lý do xóa được lưu trong nhật ký phê duyệt / tải file.";

const CertificateRequestDeleteModal = ({
  open,
  requestLabel = "",
  title = "Xóa yêu cầu cấp chứng nhận",
  description,
  busy = false,
  onClose,
  onConfirm,
}) => {
  const [reason, setReason] = useState("");
  const [countdown, setCountdown] = useState(COUNTDOWN_SECONDS);

  useEffect(() => {
    if (!open) {
      setReason("");
      setCountdown(COUNTDOWN_SECONDS);
      return undefined;
    }
    setCountdown(COUNTDOWN_SECONDS);
    let remaining = COUNTDOWN_SECONDS;
    const timer = window.setInterval(() => {
      remaining -= 1;
      setCountdown(Math.max(0, remaining));
      if (remaining <= 0) {
        window.clearInterval(timer);
      }
    }, 1000);
    return () => window.clearInterval(timer);
  }, [open]);

  if (!open) return null;

  const trimmedReason = reason.trim();
  const canConfirm = countdown === 0 && trimmedReason.length >= 5 && !busy;

  return (
    <div className="fixed inset-0 z-[280] flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="absolute inset-0 bg-slate-900/50"
        onClick={() => !busy && onClose()}
      />
      <motion.div
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        className="relative z-[290] w-full max-w-md rounded-2xl bg-white p-6 shadow-xl"
      >
        <h3 className="mb-2 font-black text-gray-800">{title}</h3>
        <p className="mb-4 text-sm leading-relaxed text-gray-600">
          {description ||
            (requestLabel
              ? `Bạn sắp xóa vĩnh viễn yêu cầu ${requestLabel}. Hành động này chỉ dành cho quản trị hệ thống; nhật ký xóa sẽ được lưu lại. ${DEFAULT_DESCRIPTION}`
              : `Bạn sắp xóa vĩnh viễn yêu cầu này. Nhật ký xóa sẽ được lưu lại trên hệ thống. ${DEFAULT_DESCRIPTION}`)}
        </p>
        <label className="mb-1 block text-[10px] font-black uppercase tracking-widest text-gray-400">
          Lý do xóa <span className="text-red-600">*</span>
        </label>
        <textarea
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder="Nhập lý do xóa (tối thiểu 5 ký tự)..."
          disabled={busy}
          className="mb-1 min-h-[96px] w-full rounded-xl border border-gray-200 p-3 text-sm disabled:opacity-60"
        />
        <p className="mb-4 text-[11px] text-gray-500">
          {trimmedReason.length < 5
            ? `Còn thiếu ${Math.max(0, 5 - trimmedReason.length)} ký tự.`
            : "Đã đủ độ dài lý do."}
        </p>
        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            disabled={busy}
            className="rounded-xl px-4 py-2 font-bold text-gray-500 disabled:opacity-50"
          >
            Hủy
          </button>
          <button
            type="button"
            disabled={!canConfirm}
            onClick={() => onConfirm(trimmedReason)}
            className="flex items-center gap-2 rounded-xl bg-red-800 px-4 py-2 font-bold text-white disabled:cursor-not-allowed disabled:opacity-50"
          >
            <Trash2 size={16} />
            {countdown > 0 ? `Xác nhận xóa (${countdown}s)` : "Xác nhận xóa"}
          </button>
        </div>
      </motion.div>
    </div>
  );
};

export default CertificateRequestDeleteModal;
